import { replaceUrlParamsWithValue } from '../utils';

export function fetchSeoInfo(reqFn, data?: any, options?: any) {
  const url = '/v1/seo/info';
  return reqFn(url, data, options);
}

export function fetchFormSettings(
  reqFn,
  data?: any,
  options?: any
): Promise<any> {
  const url = '/v1/search/formsettings';
  return reqFn(url, data, options);
}

export function getFormSettings(
  reqFn,
  data?: any,
  options?: any
): Promise<any> {
  const url = '/v1/quotes/formSettings';
  return reqFn(url, data, options);
}

export function getTestimonials(
  reqFn,
  data?: { profileId: string; categorySlug?: string; appointmentId?: string },
  options?: any
): Promise<any> {
  const { profileId, categorySlug, appointmentId } = data || {};
  const url = replaceUrlParamsWithValue('/v1/@/:profileId/testimonials', {
    profileId,
  });
  return reqFn(url, { categorySlug, appointmentId }, options);
}

export function patchReorderImages(reqFn, { albumId, mediaId, position }) {
  const url = `/v1/providers/albums/${albumId}/media/${mediaId}`;
  return reqFn(url, { position });
}

export function getBookedOutDates(reqFn, params?) {
  const url = `/v1/providers/bookedoutdates`;
  return reqFn(url, params);
}

export function postBookOutEntireDay(reqFn, { eventDate }, options?) {
  const url = `/v1/providers/bookedoutdates`;
  return reqFn(url, { eventDate }, options);
}

export function deleteBookedOutDay(reqFn, { eventDate }, options?) {
  const url = replaceUrlParamsWithValue(
    `/v1/providers/bookedoutdates/fullday/:eventDate`,
    { eventDate }
  );
  return reqFn(url, {}, options);
}

export function getCalendarAppointments(reqFn, params = {}, options?) {
  const url = `/v1/providers/appointments`;
  return reqFn(url, params, options);
}

export function getGigDetails(reqFn, { gigId }, options?) {
  const url = replaceUrlParamsWithValue(`/v1/providers/gigs/:gigId`, { gigId });
  return reqFn(url, {}, options);
}

export function getAppointmentDetails(reqFn, { appointmentId }, options?) {
  const url = replaceUrlParamsWithValue(
    `/v1/providers/appointments/:appointmentId`,
    { appointmentId }
  );
  return reqFn(url, {}, options);
}

export function getQuoteDetails(reqFn, { quoteId }, options?) {
  const url = replaceUrlParamsWithValue(`/v1/quotes/:quoteId`, { quoteId });
  return reqFn(url, {}, options);
}

export function saveQuotePreferences(patchFn, { quoteId }, data, options?) {
  const url = replaceUrlParamsWithValue('/v1/quotes/:quoteId/preferences', { quoteId });
  return patchFn(url, data, { loader: false, showToastOnError: false, ...options });
}

// Public (unauthed) endpoint that recomputes a provider's offer/pricing for a
// search using the search's current location/date/services. Returns 404 when
// the provider can no longer service the search (out of travel area, services
// removed, etc.) — callers can use that to detect "no longer serviced".
export function getProviderPricingForSearch(
  reqFn,
  { searchId, profileId }: { searchId: string; profileId: string },
  params: { mobileOrStudio?: string } = {},
  options?
) {
  const url = replaceUrlParamsWithValue(`/v1/quotes/:searchId/@/:profileId`, {
    searchId,
    profileId,
  });
  return reqFn(url, params, options);
}

// ---------------------------------------------------------------------------
// Instant Book — customer booking flow
// ---------------------------------------------------------------------------

export function getInstantBookAvailability(
  reqFn,
  { profileId, categorySlug, searchId }: { profileId: string; categorySlug: string; searchId: string },
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/instant-book/:profileId/category/:categorySlug/availability/:searchId`,
    { profileId, categorySlug, searchId }
  );
  return reqFn(url, {}, options);
}

export function postIdentifyCustomer(
  postFn,
  { profileId, categorySlug }: { profileId: string; categorySlug: string },
  data: { name: string; email: string; mobile: string; searchId: string },
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/instant-book/:profileId/category/:categorySlug/identify`,
    { profileId, categorySlug }
  );
  return postFn(url, data, options);
}

export function postPrepareInstantBookAvailability(
  postFn,
  { searchId, profileId }: { searchId: string; profileId: string },
  data: {
    name: string;
    email: string;
    mobile: string;
    finishTime?: string;
    startTime?: string;
    timeConstraint?: 'finish' | 'start';
    mobileOrStudio?: string;
    location?: object;
    addressUnknown?: boolean;
  },
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/instant-book/search/:searchId/@/:profileId/prepare-availability`,
    { searchId, profileId }
  );
  return postFn(url, data, options);
}

export function patchInstantBookTimingConstraint(
  patchFn,
  { searchId, profileId }: { searchId: string; profileId: string },
  data: (
    | { timeConstraint: 'finish'; finishTime: string }
    | { timeConstraint: 'start'; startTime: string }
  ) & { location?: object; addressUnknown?: boolean },
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/instant-book/search/:searchId/@/:profileId/timing-constraint`,
    { searchId, profileId }
  );
  return patchFn(url, data, options);
}

export function postHoldSlot(
  postFn,
  { profileId, categorySlug }: { profileId: string; categorySlug: string },
  data: any,
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/instant-book/:profileId/category/:categorySlug/hold-slot`,
    { profileId, categorySlug }
  );
  return postFn(url, data, options);
}

export function getHold(getFn, { holdId }: { holdId: string }, options?) {
  const url = replaceUrlParamsWithValue(`/v1/instant-book/hold/:holdId`, { holdId });
  return getFn(url, {}, options);
}

export function getMyHold(
  getFn,
  { profileId, categorySlug }: { profileId: string; categorySlug: string },
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/instant-book/:profileId/category/:categorySlug/my-hold`,
    { profileId, categorySlug }
  );
  return getFn(url, {}, options);
}

export function deleteHold(delFn, { holdId }: { holdId: string }, options?) {
  const url = replaceUrlParamsWithValue(`/v1/instant-book/hold/:holdId`, { holdId });
  return delFn(url, {}, options);
}

export function patchHoldRequester(
  patchFn,
  { holdId }: { holdId: string },
  data: { name: string; email: string; mobile: string },
  options?
) {
  const url = replaceUrlParamsWithValue(`/v1/instant-book/hold/:holdId/requester`, { holdId });
  return patchFn(url, data, options);
}

export function postHoldAddress(
  postFn,
  { holdId }: { holdId: string },
  data: { name?: string; mobile?: string; email?: string; location?: any | null },
  options?
) {
  const url = replaceUrlParamsWithValue(`/v1/instant-book/hold/:holdId/address`, { holdId });
  return postFn(url, data, options);
}

export function postHoldMarketingOptIn(
  postFn,
  { holdId }: { holdId: string },
  data: Record<string, never> = {},
  options?
) {
  const url = replaceUrlParamsWithValue(`/v1/instant-book/hold/:holdId/marketing-opt-in`, { holdId });
  return postFn(url, data, options);
}

export function postHoldGiftDecline(
  postFn,
  { holdId }: { holdId: string },
  data: Record<string, never> = {},
  options?
) {
  const url = replaceUrlParamsWithValue(`/v1/instant-book/hold/:holdId/gift-decline`, { holdId });
  return postFn(url, data, options);
}

export function postHoldConfirmPayment(
  postFn,
  { holdId }: { holdId: string },
  data: { paymentIntentId?: string; cardHolderName?: string },
  options?
) {
  const url = replaceUrlParamsWithValue(`/v1/instant-book/hold/:holdId/confirm-payment`, { holdId });
  return postFn(url, data, options);
}

export function postReportMissingConfirmation(
  postFn,
  { holdId }: { holdId: string },
  options?
) {
  const url = replaceUrlParamsWithValue(`/v1/instant-book/hold/:holdId/report-missing-confirmation`, { holdId });
  return postFn(url, {}, options);
}

// ---------------------------------------------------------------------------
// Instant Book — provider window management
// ---------------------------------------------------------------------------

export function getProviderEligibleCategories(getFn, options?) {
  return getFn(`/v1/instant-book/provider/eligible-categories`, {}, options);
}

export function getInstantBookAccessStatus(getFn, options?) {
  return getFn(`/v1/instant-book/provider/access-status`, {}, options);
}

export function postInstantBookRequestAccess(postFn, options?) {
  return postFn(`/v1/instant-book/provider/request-access`, {}, options);
}

export function getProviderCategoryServices(
  getFn,
  { categorySlug }: { categorySlug: string },
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/instant-book/provider/:categorySlug/services`,
    { categorySlug }
  );
  return getFn(url, {}, options);
}

export function patchProviderClustering(
  patchFn,
  { categorySlug }: { categorySlug: string },
  data: { allowClustering?: boolean; bufferMinutesClustered?: number },
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/instant-book/provider/:categorySlug/clustering`,
    { categorySlug }
  );
  return patchFn(url, data, options);
}

export function patchProviderYieldManagement(
  patchFn,
  { categorySlug }: { categorySlug: string },
  data: { mode: 'max' | 'balanced' | 'fill' },
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/instant-book/provider/:categorySlug/yield-management`,
    { categorySlug }
  );
  return patchFn(url, data, options);
}

export function patchProviderDynamicPricing(
  patchFn,
  { categorySlug }: { categorySlug: string },
  data: { isEnabled?: boolean; maxDiscountPercent?: 0 | 15 | 20 | 25 },
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/instant-book/provider/:categorySlug/dynamic-pricing`,
    { categorySlug }
  );
  return patchFn(url, data, options);
}

/** PATCH instant-book travel — tiered zones vs legacy per-km comparison (non-blocking). */
export type TravelPricingVerdict =
  | 'more_expensive'
  | 'less_expensive'
  | 'similar'
  | 'mixed'
  | 'not_applicable';

export interface ITravelOnlyCompareSpot {
  distanceKm: number;
  partySize: number;
  legacyTravel: number;
  ibTravel: number;
  delta: number;
}

export interface ITravelPricingVsLegacy {
  basis: 'travel_only';
  verdict: TravelPricingVerdict;
  hasComparableLegacy: boolean;
  isMoreExpensiveAnywhere: boolean;
  /** One-line summary for the UI (travel charges only). */
  summaryNote: string;
  maxTieredVsLegacy: ITravelOnlyCompareSpot | null;
  maxLegacyVsTiered: ITravelOnlyCompareSpot | null;
  /** Legacy shape: values are travel $ when `basis === 'travel_only'`. */
  worstCase: {
    distanceKm: number;
    partySize: number;
    legacyPreStudio: number;
    ibPreStudio: number;
    delta: number;
  } | null;
  examples: Array<{
    distanceKm: number;
    partySize: number;
    legacyPreStudio: number;
    ibPreStudio: number;
    delta: number;
  }>;
  referenceNote: string;
}

export interface IPatchInstantBookTravelResponse {
  status?: string;
  data?: {
    distanceByPartySize: {
      p1: number | null;
      p2: number | null;
      p3: number | null;
      p4: number | null;
      p5: number | null;
    } | null;
    travelPricingVsLegacy: ITravelPricingVsLegacy | null;
  };
}

export function patchProviderInstantBookTravel(
  patchFn,
  { categorySlug }: { categorySlug: string },
  data: {
    distanceByPartySize: {
      p1: number | null;
      p2: number | null;
      p3: number | null;
      p4: number | null;
      p5: number | null;
    };
  },
  options?
): Promise<IPatchInstantBookTravelResponse> {
  const url = replaceUrlParamsWithValue(
    `/v1/instant-book/provider/:categorySlug/travel`,
    { categorySlug }
  );
  return patchFn(url, data, options);
}

export interface IPreviewInstantBookTravelResponse {
  status?: string;
  data?: {
    travelPricingVsLegacy: ITravelPricingVsLegacy | null;
  };
}

/** POST preview — tiered travel vs legacy (no save). Use loader: false when debouncing in UI. */
export function postProviderInstantBookTravelPreview(
  postFn,
  { categorySlug }: { categorySlug: string },
  data: {
    distanceByPartySize: {
      p1: number;
      p2: number;
      p3: number;
      p4: number;
      p5: number;
    };
  },
  options?
): Promise<IPreviewInstantBookTravelResponse> {
  const url = replaceUrlParamsWithValue(
    `/v1/instant-book/provider/:categorySlug/travel/preview`,
    { categorySlug }
  );
  return postFn(url, data, options);
}

export function postCompleteInstantBookOnboarding(
  postFn,
  { categorySlug }: { categorySlug: string },
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/instant-book/provider/:categorySlug/complete-onboarding`,
    { categorySlug }
  );
  return postFn(url, {}, options);
}

export function getProviderWindows(
  getFn,
  { categorySlug, date }: { categorySlug: string; date?: string },
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/instant-book/provider/:categorySlug/windows`,
    { categorySlug }
  );
  return getFn(url, date ? { date } : {}, options);
}

export function postProviderWindow(
  postFn,
  { categorySlug }: { categorySlug: string },
  data: {
    date: string;
    windowStart: string;
    windowEnd: string;
    replaceOverlapping?: boolean;
  },
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/instant-book/provider/:categorySlug/windows`,
    { categorySlug }
  );
  return postFn(url, data, options);
}

export function deleteProviderWindow(
  delFn,
  { categorySlug, windowId }: { categorySlug: string; windowId: string },
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/instant-book/provider/:categorySlug/windows/:windowId`,
    { categorySlug, windowId }
  );
  return delFn(url, {}, options);
}

// ---------------------------------------------------------------------------
// Provider time blocks (personal calendar blocks)
// ---------------------------------------------------------------------------

export function getProviderTimeBlocks(
  getFn,
  {
    date,
    startDate,
    endDate,
  }: { date?: string; startDate?: string; endDate?: string } = {},
  options?
) {
  const params: Record<string, string> = {};
  if (date) params.date = date;
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  return getFn(`/v1/instant-book/provider/time-blocks`, params, options);
}

export function postProviderTimeBlock(
  postFn,
  data: {
    date: string;
    startTime: string;
    endTime: string;
    label?: string;
    location?: { coordinates: [number, number]; address?: any };
    countsAsIncome?: boolean;
    incomeAmount?: number;
  },
  options?
) {
  return postFn(`/v1/instant-book/provider/time-blocks`, data, options);
}

export function patchProviderTimeBlock(
  patchFn,
  { blockId, ...data }: {
    blockId: string;
    startTime?: string;
    endTime?: string;
    label?: string;
    location?: { coordinates: [number, number]; address?: any } | null;
    countsAsIncome?: boolean;
    incomeAmount?: number;
  },
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/instant-book/provider/time-blocks/:blockId`,
    { blockId }
  );
  return patchFn(url, data, options);
}

export function deleteProviderTimeBlock(
  delFn,
  { blockId }: { blockId: string },
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/instant-book/provider/time-blocks/:blockId`,
    { blockId }
  );
  return delFn(url, {}, options);
}

export function postProviderWindowsBulk(
  postFn,
  { categorySlug }: { categorySlug: string },
  data: {
    windowStartHHMM: string;
    windowEndHHMM: string;
    daysOfWeek: number[];
    startDate: string;
    endDate: string;
    conflictStrategy?: 'skip' | 'error' | 'replace';
  },
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/instant-book/provider/:categorySlug/windows/bulk`,
    { categorySlug }
  );
  return postFn(url, data, options);
}

export function postProviderTimeBlocksBulk(
  postFn,
  data: {
    startHHMM: string;
    endHHMM: string;
    daysOfWeek: number[];
    startDate: string;
    endDate: string;
    label?: string;
    location?: { coordinates: [number, number]; address?: any };
    countsAsIncome?: boolean;
    incomeAmount?: number;
  },
  options?
) {
  return postFn(`/v1/instant-book/provider/time-blocks/bulk`, data, options);
}

export function deleteProviderTimeBlockGroup(
  delFn,
  { groupId }: { groupId: string },
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/instant-book/provider/time-blocks/group/:groupId`,
    { groupId }
  );
  return delFn(url, {}, options);
}

// ---------------------------------------------------------------------------
// Promotions
// ---------------------------------------------------------------------------

export interface IPromotion {
  _id: string;
  startDate: string;
  endDate: string;
  minSpend: number;
  discountPercent: number;
  isEnabled: boolean;
  dailyLimit?: number | null;
  redemptionCount?: number;
  createdAt: string;
}

export function getProviderPromotions(
  getFn,
  { categorySlug }: { categorySlug: string },
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/instant-book/provider/:categorySlug/promotions`,
    { categorySlug }
  );
  return getFn(url, {}, options);
}

export function createProviderPromotion(
  postFn,
  { categorySlug }: { categorySlug: string },
  data: {
    startDate: string;
    endDate: string;
    minSpend: number;
    discountPercent: number;
    dailyLimit?: number | null;
  },
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/instant-book/provider/:categorySlug/promotions`,
    { categorySlug }
  );
  return postFn(url, data, options);
}

export function updateProviderPromotion(
  patchFn,
  { categorySlug, promoId }: { categorySlug: string; promoId: string },
  data: Partial<{
    startDate: string;
    endDate: string;
    minSpend: number;
    discountPercent: number;
    isEnabled: boolean;
    dailyLimit: number | null;
  }>,
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/instant-book/provider/:categorySlug/promotions/:promoId`,
    { categorySlug, promoId }
  );
  return patchFn(url, data, options);
}

export function deleteProviderPromotion(
  delFn,
  { categorySlug, promoId }: { categorySlug: string; promoId: string },
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/instant-book/provider/:categorySlug/promotions/:promoId`,
    { categorySlug, promoId }
  );
  return delFn(url, {}, options);
}

export function reorderProviderPromotions(
  putFn,
  { categorySlug }: { categorySlug: string },
  promotionIds: string[],
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/instant-book/provider/:categorySlug/promotions/reorder`,
    { categorySlug }
  );
  return putFn(url, { promotionIds }, options);
}

// ---------------------------------------------------------------------------
// Destinations
// ---------------------------------------------------------------------------

export function postProviderDestination(
  postFn,
  { categorySlug }: { categorySlug: string },
  data: {
    address: string;
    coordinates: [number, number];
    radiusKm: 10 | 25 | 50;
    minimumBookingValue: number;
    discountPct?: number | null;
  },
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/provider/:categorySlug/destinations`,
    { categorySlug }
  );
  return postFn(url, data, options);
}

export function patchProviderDestination(
  patchFn,
  {
    categorySlug,
    destinationId,
  }: { categorySlug: string; destinationId: string },
  data: {
    address?: string;
    coordinates?: [number, number];
    radiusKm?: 10 | 25 | 50;
    minimumBookingValue?: number;
    discountPct?: number | null;
    isEnabled?: boolean;
  },
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/provider/:categorySlug/destinations/:destinationId`,
    { categorySlug, destinationId }
  );
  return patchFn(url, data, options);
}

export function deleteProviderDestination(
  delFn,
  {
    categorySlug,
    destinationId,
  }: { categorySlug: string; destinationId: string },
  options?
) {
  const url = replaceUrlParamsWithValue(
    `/v1/provider/:categorySlug/destinations/:destinationId`,
    { categorySlug, destinationId }
  );
  return delFn(url, {}, options);
}
